import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { EncodeUrl } from '@pkg/classes';
import YatShareModule from '@pkg/components/Dashboard/ShareModule';
import ValidationInput from '@pkg/components/Input/ValidationInput/ValidationInput';
import { DepButton } from '@pkg/components/deprecated';
import { useAppDispatch } from '@pkg/hooks';
import { isValidUrl } from '@pkg/utils';

import { type ApiResult } from '@app/client';
import { type EidCategories, EidCategory, type EmojiId } from '@app/client/common/structs/emoji/emoji';
import config from '@app/config';
import { getDisplayUrl, type RedirectUrlItems } from '@app/helpers';
import { updateEmojiTag } from '@app/store/actions/emoji';
import { getCurrentUserEmojis, useEmojisStore } from '@app/store/zustand/emojis/useEmojisStore';

import PagePreview from './PagePreview';
import './YatRedirectForm.scss';

const frontendUrl = config.publicUrl;

interface YatRedirectFormProps {
    closeForm: () => void;
    isVisible?: boolean;
}

const noUrlSet = { url: '', displayUrl: '', protocol: 'https' };

const YatRedirectForm = ({ closeForm, isVisible }: YatRedirectFormProps) => {
    const { t } = useTranslation(['power_ups', 'words']);
    const emojiID = useEmojisStore((state) => state.currentEmoji);
    const tags = useEmojisStore((state) => state.tags);
    const [currentRedirectUrl, setCurrentRedirectUrl] = useState<RedirectUrlItems>(noUrlSet);
    const [newRedirectUrl, setNewRedirectUrl] = useState<RedirectUrlItems>(noUrlSet);
    const [isNewUrlValid, setIsNewUrlValid] = useState<boolean>(false);
    const [isUpdateComplete, setIsUpdateComplete] = useState<boolean>(false);
    const [wasVisible, setWasVisible] = useState<boolean>(false);
    const [debounce, setDebounce] = useState<number>();
    const topAnchorRef = useRef<HTMLInputElement>(null);
    const dispatch = useAppDispatch();

    useEffect(() => {
        return () => {
            // Reset state on close
            setIsNewUrlValid(false);
            setCurrentRedirectUrl({ url: '', displayUrl: '', protocol: 'https' });
        };
    }, []);

    // Load current redirect URL
    useEffect(() => {
        const category = '0x' + (+EidCategory.REDIRECT).toString(16).padStart(4, '0');
        const latestUrl = [...tags].filter((t) => t.tag === category).pop();
        setCurrentRedirectUrl(latestUrl ? getDisplayUrl(latestUrl.data) : noUrlSet);
        setNewRedirectUrl(noUrlSet);
    }, [tags]);

    const handleScrollToTop = useCallback((): void => {
        if (!isVisible && topAnchorRef && topAnchorRef.current) {
            topAnchorRef.current.scrollIntoView();
        }
    }, [isVisible]);

    // Reset the state when panel is closed
    // Use timer to make sure state is changed when panel
    // ie already hidden
    useEffect(() => {
        if (wasVisible !== isVisible) {
            window.setTimeout(function () {
                handleResetState();
                setWasVisible(isVisible || false);
                if (wasVisible) {
                    handleScrollToTop();
                }
            }, 500);
        }
    }, [isVisible, wasVisible, handleScrollToTop]);

    const handleCloseForm = useCallback((): void => closeForm(), [closeForm]);

    const decodeURLRecursively = useCallback((url, maxDepth = 3) => {
        if (url.indexOf('%') !== -1 && maxDepth > 0) {
            try {
                return decodeURLRecursively(decodeURIComponent(url), --maxDepth);
            } catch {
                return url;
            }
        }
        return url;
    }, []);

    // Handle form submit
    const handleSubmit = useCallback(async (): Promise<boolean> => {
        const category = '0x' + (+EidCategory.REDIRECT).toString(16).padStart(4, '0');
        const deleteTags = tags.filter((t) => t.tag === category).map((t) => t.hash);
        const redirectUrlTagCategory = '4';
        const url = new EncodeUrl(newRedirectUrl.url);

        const insert = [{ tag: redirectUrlTagCategory, data: url.encode() }] as unknown as EidCategories[];

        const dispatchedPromise = dispatch(updateEmojiTag(emojiID, insert, deleteTags)) as unknown as Promise<
            ApiResult<EmojiId[]>
        >;

        return dispatchedPromise
            .then((r) => {
                if (r.ok) {
                    setIsUpdateComplete(true);
                    getCurrentUserEmojis(dispatch);
                    return true;
                }
                return false;
            })
            .catch(() => {
                return false;
            })
            .finally(() => {
                return true;
            });
    }, [emojiID, dispatch, newRedirectUrl, tags]);

    const handleResetState = (): void => {
        setNewRedirectUrl(noUrlSet);
        setIsNewUrlValid(false);
        setIsUpdateComplete(false);
    };

    const validateRedirectUrl = useCallback(
        (displayUrl: RedirectUrlItems): void => {
            if (
                displayUrl.url === '' ||
                !isValidUrl(encodeURI(displayUrl.url)) ||
                displayUrl.url === currentRedirectUrl.url
            ) {
                setIsNewUrlValid(false);
            } else {
                setIsNewUrlValid(true);
            }
        },
        [currentRedirectUrl.url],
    );

    const headline = isUpdateComplete ? t('redirect_success') : t('what_site', { frontendUrl, emojiID });
    const subHeadline = isUpdateComplete ? t('redirect_success_explainer_copy') : t('redirect_url_explainer_copy');

    const button = useMemo(() => {
        return isUpdateComplete ? (
            <DepButton classes="yat-redirect-form__button" variant="secondary" size="large" onClick={handleCloseForm}>
                {t('done')}
            </DepButton>
        ) : (
            <DepButton
                classes="yat-redirect-form__button"
                disabled={!isNewUrlValid}
                variant="secondary"
                size="large"
                onClick={handleSubmit}
            >
                {t('link_up')}
            </DepButton>
        );
    }, [isUpdateComplete, isNewUrlValid, handleSubmit, handleCloseForm, t]);

    const inputElement = useMemo(() => {
        // Handle input typing
        const handleChange = (event): void => {
            const { value } = event.target;
            const displayUrl = getDisplayUrl(value);
            setNewRedirectUrl(displayUrl);
            setCurrentRedirectUrl(noUrlSet);
            clearTimeout(debounce);
            const timeout = window.setTimeout(function () {
                validateRedirectUrl(displayUrl);
            }, 1000);
            setDebounce(timeout);
        };

        // Handle input blur
        const handleBlur = (event): void => {
            const { value } = event.target;
            if (value.length > 0) {
                const displayUrl = getDisplayUrl(value);
                validateRedirectUrl(displayUrl);
            }
        };

        return isUpdateComplete ? (
            <YatShareModule yat={emojiID} classes="yat-redirect__share-module" />
        ) : (
            <div className="yat-redirect-form__input">
                <ValidationInput
                    label="URL"
                    type="url"
                    placeholder={t('your_website_placeholder')}
                    onKeyDown={(e) => {
                        e.key === 'Enter' && e.preventDefault();
                    }}
                    onChange={(event) => handleChange(event)}
                    onBlur={(event) => handleBlur(event)}
                    value={decodeURLRecursively(newRedirectUrl.displayUrl || currentRedirectUrl.displayUrl)}
                    isValid={isNewUrlValid}
                />
            </div>
        );
    }, [
        isUpdateComplete,
        emojiID,
        newRedirectUrl.displayUrl,
        currentRedirectUrl.displayUrl,
        isNewUrlValid,
        decodeURLRecursively,
        debounce,
        validateRedirectUrl,
        t,
    ]);

    return (
        <div className="yat-redirect-form" ref={topAnchorRef}>
            <h2>{headline}</h2>
            <p className="body-copy">{subHeadline}</p>

            <div className="yat-redirect-form__canvas">
                <div className="preview-container">
                    <div className="configuration-preview">
                        <div className="redirect-preview">
                            <PagePreview
                                redirectUrl={newRedirectUrl.displayUrl ? newRedirectUrl : currentRedirectUrl}
                            />
                        </div>
                    </div>
                </div>
                <div className="yat-redirect-form__input-container">{inputElement}</div>
                <div className="yat-redirect-form__button">{button}</div>
                <div className="yat-redirect-form__spacer" />
            </div>
        </div>
    );
};

export default YatRedirectForm;
